import { useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import {
	showQuickstart,
	useAttributesStore,
} from "@/dentlab/src/store/Attributes";
import { getSetupStatus } from "@/dentlab/src/store/Central/selectors";
import { RolesEnum } from "@/lib/types/roles.types";
import { SETUP_TASKS_COUNT } from "@/dentlab/src/pages/quickstart/quickstart-tasks";
import { CircularProgressCount } from "@/dentlab/src/layout/layout-components/app-bar/app-bar-components/circular-progress-count.component";
import {
	SheetContentType,
	useSheetStore,
} from "@/dentlab/src/store/Sheets/sheet.store";

export const QuickstartDashboardBanner: React.FC = () => {
	const postHog = usePostHog();
	const isQuickstartEnabled = useFeatureFlagEnabled("quickstart");
	const {
		role,
		organization,
		profile,
		activatedRegions,
		bankAccountsLookup,
		priceLists,
	} = useCentralStore((state) => ({
		role: state.role,
		organization: state.organization,
		profile: state.profile,
		activatedRegions: state.activatedRegions,
		bankAccountsLookup: state.bankAccountsLookup,
		priceLists: state.priceLists,
	}));

	const { progress, getQuickstartCompletedTaskIds } = useAttributesStore(
		(state) => ({
			progress: state.getQuickstartProgress(),
			getQuickstartCompletedTaskIds: state.getQuickstartCompletedTaskIds,
		})
	);

	const openSheet = useSheetStore((state) => state.openSheet);

	const setupStatus = useMemo(() => {
		if (role !== RolesEnum.OWNER) return null;
		return getSetupStatus(
			organization,
			profile,
			activatedRegions,
			bankAccountsLookup,
			priceLists
		);
	}, [
		role,
		organization,
		profile,
		activatedRegions,
		bankAccountsLookup,
		priceLists,
	]);

	const isShowQuickstart = useMemo(() => {
		if (role === RolesEnum.OWNER && setupStatus?.isIncomplete === true) {
			return true;
		}
		if (!isQuickstartEnabled) return false;
		return showQuickstart(
			role,
			getQuickstartCompletedTaskIds(),
			setupStatus?.isIncomplete
		);
	}, [
		role,
		isQuickstartEnabled,
		getQuickstartCompletedTaskIds,
		setupStatus?.isIncomplete,
	]);

	if (!isShowQuickstart) return null;

	const setupCompleted = setupStatus
		? setupStatus.steps.filter((s) => s.isComplete).length
		: 0;
	const totalCompleted =
		(isQuickstartEnabled ? progress.completed : 0) +
		(setupStatus ? setupCompleted : 0);
	const totalTasks =
		(isQuickstartEnabled ? progress.total : 0) +
		(setupStatus ? SETUP_TASKS_COUNT : 0);

	const handleClick = () => {
		postHog.capture("open_quickstart", {
			progress_completed: totalCompleted,
			progress_total: totalTasks,
		});
		openSheet(SheetContentType.QUICKSTART);
	};

	return (
		<div
			className="flex items-center gap-2 py-2 pl-2 pr-3 rounded-md border cursor-pointer transition-colors bg-indigo-50 border-indigo-200 hover:bg-indigo-100"
			onClick={handleClick}
			title="Erste Schritte"
			aria-label="quickstart-dashboard-banner"
			data-testid="quickstart-dashboard-banner"
		>
			<div className="w-6 h-6 flex items-center justify-center">
				<CircularProgressCount
					completed={totalCompleted}
					total={totalTasks}
					size={20}
				/>
			</div>
			<div className="flex items-center gap-2">
				<div className="flex-1">
					<div className="text-xs font-medium text-indigo-900">
						Erste Schritte
					</div>
					<div className="text-xs flex items-center gap-1 text-indigo-700">
						Einrichtung fortsetzen
						<ArrowRight className="h-3 w-3" />
					</div>
				</div>
			</div>
		</div>
	);
};
